import { useState, useEffect, useRef, memo } from "react";
import { Link } from "react-router-dom";
import { PropTypes } from "prop-types";
import Isotope from "isotope-layout";
import Pagination from "./HelperComponents/Pagination";
import Filter from "../Portfolio/Filter";
import { BookSVG } from "../../Components/BookSVG";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../hooks/useLanguage";
import { buildBlogPath } from "../../utils/languageUtils";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "Máj",
    "Jún",
    "Júl",
    "Aug",
    "Sep",
    "Okt",
    "Nov",
    "Dec",
  ];
  return `${months[date.getMonth()]} ${date.getDate()}`;
};

const calculateReadTime = (content) => {
  if (!content) return 1;
  const wordsPerMinute = 200;
  let totalWords = 0;

  const countWords = (text) =>
    text?.trim().split(/\s+/).filter(Boolean).length || 0;

  const processBlock = (block) => {
    if (!block) return 0;
    let words = 0;

    switch (block._type) {
      case "block":
        if (block.children) {
          block.children.forEach((child) => {
            if (child.text) {
              words += countWords(child.text);
            }
          });
        }
        break;
      case "image":
        words += 10;
        break;
      case "customTweet":
        words += 30;
        break;
      case "pollReference":
        if (block.poll) {
          words += countWords(block.poll.question);
          block.poll.options?.forEach((option) => {
            words += countWords(option);
          });
          words += countWords(block.poll.caption);
        }
        break;
    }
    return words;
  };

  if (Array.isArray(content.body)) {
    content.body.forEach((block) => {
      totalWords += processBlock(block);
    });
  }

  if (content.title) {
    totalWords += countWords(content.title);
  }
  if (content.excerpt) {
    totalWords += countWords(content.excerpt);
  }

  return Math.max(1, Math.ceil(totalWords / wordsPerMinute));
};

const BlogOverlayImg = (props) => {
  const { t } = useTranslation(['common']);
  const { currentLang } = useLanguage();
  const blogWrapper = useRef();
  const [isotope, setIsotope] = useState(null);

  useEffect(() => {
    if (blogWrapper.current) {
      const iso = new Isotope(blogWrapper.current, {
        itemSelector: ".grid-item",
        layoutMode: "masonry",
        masonry: { columnWidth: ".grid-sizer" },
        percentPosition: true,
      });
      setIsotope(iso);
      return () => iso.destroy();
    }
  }, []);

  const handleFilterChange = () => {
    if (isotope) {
      isotope.arrange({ filter: "*" });
    }
  };

  if (!props.data?.length) {
    return (
      <div className='flex justify-center items-center min-h-[200px]'>
        <div className='animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900' />
      </div>
    );
  }

  return (
    <div className='grid-wrapper'>
      {props.filter !== false && (
        <Filter
          title={props.title}
          filterData={props.filterData}
          onFilterChange={handleFilterChange}
        />
      )}
      <div ref={blogWrapper} className='blog-grid-container'>
        <div className='grid-sizer' />
        {props.data.map((item, i) => {
          const category = item.category?.[0];
          const displayReadingTime = Array.isArray(item.body) && item.body.length > 0
            ? calculateReadTime({
                body: item.body,
                title: item.title,
                excerpt: item.excerpt,
              })
            : item.readTime || calculateReadTime({
                body: item.body,
                title: item.title,
                excerpt: item.excerpt,
              });

          // Skip items without a valid slug
          if (!item.slug?.current) {
            console.warn("Blog post missing slug:", item);
            return null;
          }

          const postPath = buildBlogPath(currentLang, item.slug.current);

          return (
            <div key={i} className='grid-item'>
              <div className='bg-white rounded-xl overflow-hidden h-[480px] md:h-[380px] flex flex-col inaq-blog-card '>
                <Link
                  to={postPath}
                  className='block aspect-[16/9] overflow-hidden md:!mx-2 xs:!mx-0 group [transform:translateZ(0)]'
                >
                  <img
                    src={item.img}
                    alt={item.title}
                    className='w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 [backface-visibility:hidden]'
                  />
                </Link>
                <div className='p-6 pt-4 flex flex-col flex-grow blog-section-title'>
                  <span className='blog-category font-rubik bg-transparent text-inaqgold py-0 md:!py-2 rounded-md text-xs inline-block w-fit blog-section-category'>
                    {category?.title || category}
                  </span>
                  <Link
                    to={postPath}
                    className='block text-[20px] sm:text-[16px] tracking-tight leading-[1.4] font-["Oxanium"] font-semibold text-gray-900 mb-3 hover:text-gray-700 normal-case line-clamp-none md:line-clamp-3'
                  >
                    {item.title}
                  </Link>
                  <p className='text-gray-500 font-rubik !leading-[1.8] line-clamp-3 md:line-clamp-2 !text-[14px] text-base'>
                    {item.excerpt}
                  </p>
                  <div className='mt-auto flex flex-col gap-2 blog-section-date-reading-time'>
                    <div className='flex items-center text-sm xs:pt-4 text-inaqgold'>
                      <span>{formatDate(item.date)}</span>
                      <span className='mx-2 '>•</span>
                      <BookSVG className='w-4 h-4 mr-2' />
                      <span>{displayReadingTime} {t('common:blog.readTime', 'min čítania')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {props.pagination === true && (
        <div className='flex justify-center mt-[7.5rem] md:mt-20'>
          <Pagination />
        </div>
      )}
    </div>
  );
};

BlogOverlayImg.propTypes = {
  pagination: PropTypes.bool,
  title: PropTypes.string,
  grid: PropTypes.string,
  filter: PropTypes.bool,
  filterData: PropTypes.array,
  data: PropTypes.arrayOf(
    PropTypes.exact({
      _id: PropTypes.string,
      slug: PropTypes.shape({
        current: PropTypes.string,
      }),
      category: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
        })
      ),
      tags: PropTypes.array,
      blogType: PropTypes.string,
      img: PropTypes.string,
      title: PropTypes.string,
      content: PropTypes.string,
      excerpt: PropTypes.string,
      body: PropTypes.array,
      author: PropTypes.number,
      likes: PropTypes.number,
      comments: PropTypes.number,
      date: PropTypes.string,
      double_col: PropTypes.bool,
      readTime: PropTypes.number, // Added optional readTime prop
    })
  ),
};

BlogOverlayImg.defaultProps = {
  filter: false,
  data: [],
};

export default memo(BlogOverlayImg);
